import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle, Edit3, ArrowRight, Plus, X, Check } from 'lucide-react'
import { WaveIcon } from '@/components/common/WaveIcon'
import { engineApiClient } from '@/lib/apiClient'
import type { ConfirmationSummaryResponse, RoleHighlights } from '@/lib/contracts'

const FALLBACK_PROJECT_ID = 'alpha-grid'

const WHATS_INCLUDED = [
  'Full Job Description',
  'Executive Summary',
  'Search Brief',
  'Interview Scorecard',
  'Outreach Brief',
]

// ─── Inline editable list ─────────────────────────────────────────────────────
// Each item in edit mode is a real text input — click in, type over, done.

interface EditableListProps {
  items: string[]
  onSave: (items: string[]) => Promise<void>
  label: string
  bulletEl: React.ReactNode
  /** Optionally controlled from outside (header Edit button) */
  externalEdit?: boolean
  onExternalEditChange?: (val: boolean) => void
}

const EditableList = ({ items, onSave, label, bulletEl, externalEdit, onExternalEditChange }: EditableListProps) => {
  const [internalEditing, setInternalEditing] = useState(false)
  const editing = externalEdit !== undefined ? externalEdit : internalEditing
  const setEditing = (val: boolean) => {
    if (onExternalEditChange) onExternalEditChange(val)
    setInternalEditing(val)
  }
  // draft is a mutable copy; each entry is a string
  const [draft, setDraft] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const addInputRef = useRef<HTMLInputElement>(null)

  // Enter edit mode — copy current items into draft
  const startEdit = () => {
    setDraft([...items])
    setEditing(true)
    // Focus first input after render
    setTimeout(() => {
      const first = document.querySelector<HTMLInputElement>('[data-edit-item="0"]')
      first?.focus()
    }, 50)
  }

  // Update a single line
  const updateLine = (idx: number, value: string) => {
    setDraft((prev) => prev.map((v, i) => (i === idx ? value : v)))
  }

  // Remove a line
  const removeLine = (idx: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== idx))
  }

  // Add a blank line at the end then focus it
  const addLine = () => {
    setDraft((prev) => [...prev, ''])
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>('[data-edit-item]')
      inputs[inputs.length - 1]?.focus()
    }, 30)
  }

  // Keyboard: Enter on last item → add new line; Backspace on empty item → remove it
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If on last item, add new line
      if (idx === draft.length - 1) {
        addLine()
      } else {
        // Focus next
        const next = document.querySelector<HTMLInputElement>(`[data-edit-item="${idx + 1}"]`)
        next?.focus()
      }
    }
    if (e.key === 'Backspace' && draft[idx] === '' && draft.length > 1) {
      e.preventDefault()
      removeLine(idx)
      // Focus previous
      setTimeout(() => {
        const prev = document.querySelector<HTMLInputElement>(`[data-edit-item="${idx - 1}"]`)
        prev?.focus()
      }, 30)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    // Strip blank lines before saving
    const cleaned = draft.map((v) => v.trim()).filter(Boolean)
    try {
      await onSave(cleaned)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setDraft([])
    setEditing(false)
  }

  // Sync read-mode display when parent updates after save
  const displayItems = editing ? draft : items

  return (
    <div>
      {!editing ? (
        // ── Read mode ───────────────────────────────────────────────────────
        <div>
          {items.length > 0 ? (
            <ul className="space-y-2">
              {items.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2.5 text-sm group"
                >
                  {bulletEl}
                  <span className="text-gray-300 flex-1">{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm italic">None captured yet</p>
          )}
          <button
            onClick={startEdit}
            className="mt-4 flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#FD802E] transition-colors"
          >
            <Edit3 size={11} />
            Edit {label}
          </button>
        </div>
      ) : (
        // ── Edit mode — each line is a real text input ───────────────────
        <div className="space-y-1.5">
          {displayItems.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="shrink-0 opacity-40">{bulletEl}</div>
              <input
                data-edit-item={idx}
                type="text"
                value={item}
                onChange={(e) => updateLine(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                placeholder={`${label}…`}
                className="flex-1 bg-[#02182B] border border-[#FD802E]/40 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] focus:ring-1 focus:ring-[#FD802E]/30 transition-colors"
              />
              <button
                onClick={() => removeLine(idx)}
                disabled={displayItems.length === 1}
                className="text-gray-600 hover:text-red-400 transition-colors shrink-0 disabled:opacity-20"
                title="Remove line"
              >
                <X size={13} />
              </button>
            </div>
          ))}

          {/* Add new line */}
          <button
            ref={addInputRef}
            onClick={addLine}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#FD802E] transition-colors mt-1 pl-7"
          >
            <Plus size={12} /> Add {label.toLowerCase()}
          </button>

          {/* Action row */}
          <div className="flex items-center gap-3 pt-3 border-t border-[#2a4a5c] mt-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 bg-[#FD802E] text-[#233D4C] font-semibold text-xs px-4 py-2 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
            >
              <Check size={12} />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              onClick={handleCancel}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              Cancel
            </button>
            <span className="ml-auto text-xs text-gray-600">Enter to add line · Backspace on empty to remove</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── EditableSection — card with Edit button in header ────────────────────────

interface EditableSectionProps {
  title: string
  label: string
  items: string[]
  bulletEl: React.ReactNode
  onSave: (items: string[]) => Promise<void>
}

const EditableSection = ({ title, label, items, bulletEl, onSave }: EditableSectionProps) => {
  const [editing, setEditing] = useState(false)

  return (
    <div className={`bg-[#233D4C] rounded-lg p-5 border transition-colors ${
      editing ? 'border-[#FD802E]/40' : 'border-[#2a4a5c]'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">{title}</h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 text-gray-500 hover:text-[#FD802E] text-xs transition-colors px-2 py-1 rounded hover:bg-[#FD802E]/10"
          >
            <Edit3 size={12} /> Edit
          </button>
        )}
      </div>
      <EditableList
        label={label}
        items={items}
        bulletEl={bulletEl}
        onSave={onSave}
        externalEdit={editing}
        onExternalEditChange={setEditing}
      />
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const Confirmation = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const navigate = useNavigate()

  const [summary, setSummary] = useState<ConfirmationSummaryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    setIsLoading(true)
    engineApiClient
      .getConfirmationSummary(projectId)
      .then(setSummary)
      .catch(() => setError('Unable to load role summary. Please try again.'))
      .finally(() => setIsLoading(false))
  }, [projectId, retryCount])

  const patchHighlights = async (partial: Partial<RoleHighlights>) => {
    if (!summary) return
    const updated: RoleHighlights = {
      mustHaves: partial.mustHaves ?? summary.highlights.mustHaves,
      niceToHaves: partial.niceToHaves ?? summary.highlights.niceToHaves,
      leadershipSignals: partial.leadershipSignals ?? summary.highlights.leadershipSignals,
    }
    const thread = await engineApiClient.patchIntakeHighlights(projectId, updated)
    // Reflect the updated highlights immediately without a full refetch
    setSummary((prev) => prev ? { ...prev, highlights: thread.highlights } : prev)
  }

  const handleGenerate = () => {
    engineApiClient.advanceProjectStage(projectId, 'outputs').catch(() => {})
    navigate(`/project/${projectId}/output`)
  }

  return (
    <div className="bg-[#02182B] min-h-screen">
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-white">Role Confirmation</h1>
          <p className="text-gray-400 text-sm mt-1">
            Review and edit captured requirements before generating outputs
            {summary && (
              <span className="ml-2 text-gray-500">
                · {summary.intakeMessageCount} intake exchange{summary.intakeMessageCount !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {isLoading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading summary…</div>
        ) : error ? (
          <div className="max-w-md mx-auto mt-16 text-center space-y-4">
            <p className="text-gray-400 text-sm">{error}</p>
            <button
              onClick={() => { setError(null); setRetryCount(c => c + 1) }}
              className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : summary ? (
          <div className="flex flex-col lg:flex-row gap-8">

            <div className="flex-1 min-w-0 space-y-5">

              {/* Must-Haves */}
              <EditableSection
                title="Must-Haves"
                label="Must-Have"
                items={summary.highlights.mustHaves}
                bulletEl={<CheckCircle size={14} className="text-green-400 mt-0.5 shrink-0" />}
                onSave={(items) => patchHighlights({ mustHaves: items })}
              />

              {/* Nice-to-Haves */}
              <EditableSection
                title="Nice-to-Haves"
                label="Nice-to-Have"
                items={summary.highlights.niceToHaves}
                bulletEl={<span className="w-3.5 h-3.5 rounded-full border border-gray-400 mt-0.5 shrink-0 inline-block" />}
                onSave={(items) => patchHighlights({ niceToHaves: items })}
              />

              {/* Leadership Signals */}
              <EditableSection
                title="Leadership Signals"
                label="Signal"
                items={summary.highlights.leadershipSignals}
                bulletEl={<span className="text-[#FD802E] mt-0.5 shrink-0">◆</span>}
                onSave={(items) => patchHighlights({ leadershipSignals: items })}
              />

              {/* Dealbreakers — from Erica acknowledgements */}
              {summary.dealbreakersContext && summary.dealbreakersContext.length > 0 && (
                <div className="bg-[#233D4C] border border-red-500/20 rounded-lg p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <span className="text-red-400">⛔</span> Dealbreakers
                  </h3>
                  <ul className="space-y-2">
                    {summary.dealbreakersContext.map((note, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-300">
                        <span className="text-red-400 mt-0.5 shrink-0">✕</span>
                        {note}
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-500 text-xs mt-3">Captured from intake conversation</p>
                </div>
              )}

              {/* Why This Role — from Erica acknowledgements */}
              {summary.whyRoleContext && summary.whyRoleContext.length > 0 && (
                <div className="bg-[#233D4C] border border-blue-500/20 rounded-lg p-5">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <span className="text-blue-400">💡</span> Why This Role
                  </h3>
                  <ul className="space-y-2">
                    {summary.whyRoleContext.map((note, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-300">
                        <span className="text-blue-400 mt-0.5 shrink-0">→</span>
                        {note}
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-500 text-xs mt-3">Captured from intake conversation</p>
                </div>
              )}

              {/* Key themes from intake */}
              {summary.probeThemes.length > 0 && (
                <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
                  <h3 className="text-white font-semibold mb-4">Key Themes</h3>
                  <div className="flex flex-wrap gap-2">
                    {summary.probeThemes.map((theme) => (
                      <span key={theme} className="px-3 py-1 rounded-full bg-[#FD802E]/10 text-[#FD802E] text-xs font-medium border border-[#FD802E]/20">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Synthesis notes */}
              {summary.probeSynthesisNotes.length > 0 && (
                <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
                  <h3 className="text-white font-semibold mb-4">Intake Summary</h3>
                  <ul className="space-y-2">
                    {summary.probeSynthesisNotes.map((note, i) => (
                      <li key={i} className="bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-2.5 text-sm text-gray-300 leading-relaxed">
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>

            {/* Right sidebar */}
            <div className="w-full lg:w-72 lg:flex-shrink-0 space-y-4">

              {summary.readyForOutputs ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-400" />
                    <p className="text-white font-semibold text-sm">Ready to Generate</p>
                  </div>
                  <p className="text-gray-400 text-xs">All required information confirmed</p>
                </div>
              ) : (
                <div className="bg-[#FD802E]/10 border border-[#FD802E]/20 rounded-lg p-5 space-y-2">
                  <p className="text-white font-semibold text-sm">Almost ready</p>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    Complete your intake conversation and ensure at least one must-have is captured.
                  </p>
                  <button
                    onClick={() => navigate(`/project/${projectId}/intake`)}
                    className="text-[#FD802E] hover:text-[#ff8f45] text-xs transition-colors"
                  >
                    Continue intake →
                  </button>
                </div>
              )}

              <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4">
                <p className="text-xs uppercase text-gray-400 font-medium mb-3">What's Included</p>
                <ul className="space-y-2">
                  {WHATS_INCLUDED.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle size={13} className="text-[#FD802E] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 flex items-center gap-2">
                <WaveIcon size="sm" className="shrink-0" />
                <p className="text-gray-400 text-sm">Outputs ready in ~30 seconds</p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={!summary.readyForOutputs}
                className="w-full flex items-center justify-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold px-5 py-3 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm & Generate Outputs <ArrowRight size={16} />
              </button>

            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
